import { TFunction } from 'i18next';
import * as yup from 'yup';
import { StringSchema } from 'yup';
import { isZipCodePresent } from '@utils/postalCodes';
import { CountryOption, PostalCodeOption } from '@packlink/address-selectors';

export interface IAddressBaseSchema {
    company: StringSchema;
    email: StringSchema;
    firstName: StringSchema;
    phone: StringSchema;
    street1: StringSchema;
    street2: StringSchema;
    lastName: StringSchema;
}

export interface IAddressEditSchema extends IAddressBaseSchema {
    country: yup.ObjectSchema<CountryOption>;
    postalCode: yup.ObjectSchema<PostalCodeOption>;
}
export interface IAddressSchema extends IAddressBaseSchema {
    city: StringSchema;
    zipCode: StringSchema;
    alpha2Code: StringSchema;
}

/**
 * Used only in Address component. Gives an imperative message not meant for a form.
 */
export const addressSchema = (t: TFunction): IAddressSchema => ({
    alpha2Code: yup.string().trim().required(t('address.field-country.error-required')),
    city: yup.string().trim().required(),
    company: yup
        .string()
        .trim()
        .max(40, t('address.field-company.error-max-length', { count: 40 })),
    email: yup
        .string()
        .trim()
        .email(t('address.field-email.error-email'))
        .required(t('address.field-email.error-required')),
    firstName: yup
        .string()
        .trim()
        .max(40, t('address.field-first-name.error-max-length', { count: 40 }))
        .required(t('address.field-first-name.error-required')),
    lastName: yup
        .string()
        .trim()
        .max(40, t('address.field-last-name.error-max-length', { count: 40 }))
        .required(t('address.field-last-name.error-required')),
    phone: yup
        .string()
        .trim()
        .min(1, t('address.field-phone.error-min-length'))
        .max(20, t('address.field-phone.error-max-length'))
        .matches(/^[^a-zA-Z]+$/, t('address.field-phone.error-regexp'))
        .required(t('address.field-phone.error-required')),
    street1: yup
        .string()
        .trim()
        .max(40, t('address.field-address-line-1.error-max-length', { count: 40 }))
        .required(t('address.field-address-line-1.error-required')),
    street2: yup
        .string()
        .trim()
        .max(40, t('address.field-address-line-2.error-max-length', { count: 40 })),
    zipCode: yup
        .string()
        .required(t('address-form.field-location.error-required'))
        .when(['alpha2Code'], (alpha2Code: string) =>
            yup
                .string()
                .test(
                    'location',
                    t('address-form.field-location.error-invalid'),
                    async (zipCode: string | null | undefined) => {
                        if (!zipCode) return false;
                        return await isZipCodePresent(alpha2Code, zipCode);
                    },
                ),
        ),
});

export const addressEditSchema = (t: TFunction): IAddressEditSchema => ({
    country: yup.object<CountryOption>().nullable().required(t('address-form.field-country.error-required')),
    postalCode: yup
        .object<PostalCodeOption>()
        .nullable()
        .required(
            t('address-form.field-location.error-required', { field: t('address-form.field-location.error-invalid') }),
        ),
    company: yup
        .string()
        .trim()
        .max(40, t('address-form.field-company.error-max-length', { count: 40 })),
    email: yup
        .string()
        .trim()
        .email(t('address-form.field-email.error-email'))
        .required(t('address-form.field-email.error-required')),
    firstName: yup
        .string()
        .trim()
        .max(40, t('address-form.field-first-name.error-max-length', { count: 40 }))
        .required(t('address-form.field-first-name.error-required')),
    lastName: yup
        .string()
        .trim()
        .max(40, t('address-form.field-last-name.error-max-length', { count: 40 }))
        .required(t('address-form.field-last-name.error-required')),
    phone: yup
        .string()
        .trim()
        .min(1, t('address-form.field-phone.error-min-length'))
        .max(20, t('address-form.field-phone.error-max-length'))
        .matches(/^[^a-zA-Z]+$/, t('address-form.field-phone.error-regexp'))
        .required(t('address-form.field-phone.error-required')),
    street1: yup
        .string()
        .trim()
        .max(40, t('address-form.field-address-line-1.error-max-length', { count: 40 }))
        .required(t('address-form.field-address-line-1.error-required')),
    street2: yup
        .string()
        .trim()
        .max(40, t('address-form.field-address-line-2.error-max-length', { count: 40 })),
});
